/**
 * Anonymní statistika používání – jen počty, bez jakéhokoli identifikátoru.
 *
 * Telefon pošle jednou „nová instalace“ a pak nejvýš jednou za den, týden a měsíc
 * „aplikace byla použita“ (+ druh telefonu, zda běží z plochy, verzi a druh licence).
 * Žádné ID zařízení, jméno, data o včelstvech ani poloha se neposílají; schránka
 * neukládá ani IP adresu. Odesílání jde v Nastavení vypnout.
 */
import { useEffect, useState } from 'react';
import { getSetting, setSetting } from '../db/db';
import { todayISO } from '../db/rules';
import { isoWeek } from '../lib/dates';
import { FEEDBACK_URL } from './feedback';
import { loadLicence } from './licence';
import { isStandalone, platform } from './recommend';
import { plural } from './labels';
import { Empty, Notice, Screen, Section } from './ui';
import { Icon } from './icons';
import { saved } from './confirm';

const STATS_URL = FEEDBACK_URL.replace(/feedback$/, 'stats');
const SENT = 'statsSent';
const OFF = 'statsOff';

type Ev = 'install' | 'day' | 'week' | 'month';
interface Sent {
  install?: boolean;
  day?: string;
  week?: string;
  month?: string;
}

let running = false;
async function report() {
  if (running || !navigator.onLine) return;
  // vývoj a testy na vlastním počítači se nepočítají
  if (location.hostname === 'localhost' || location.hostname === '127.0.0.1') return;
  running = true;
  try {
    if (await getSetting<boolean>(OFF)) return;
    const sent: Sent = (await getSetting<Sent>(SENT)) ?? {};
    const today = todayISO();
    const due: [Ev, Partial<Sent>][] = [];
    if (!sent.install) due.push(['install', { install: true }]);
    if (sent.day !== today) due.push(['day', { day: today }]);
    if (sent.week !== isoWeek(today)) due.push(['week', { week: isoWeek(today) }]);
    if (sent.month !== today.slice(0, 7)) due.push(['month', { month: today.slice(0, 7) }]);
    if (!due.length) return;
    const lic = await loadLicence();
    const base = { p: platform() ?? 'other', s: isStandalone() ? 1 : 0, v: __APP_VERSION__.split('+')[0], l: lic.status.kind };
    for (const [e, mark] of due) {
      const r = await fetch(STATS_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ e, ...base }) }).catch(
        () => null,
      );
      if (!r?.ok) break; // zkusí se příště
      Object.assign(sent, mark);
      await setSetting(SENT, { ...sent });
    }
  } finally {
    running = false;
  }
}

export function installStats() {
  addEventListener('load', () => setTimeout(() => void report().catch(() => {}), 4000));
  addEventListener('online', () => void report().catch(() => {}));
  document.addEventListener('visibilitychange', () => document.visibilityState === 'visible' && void report().catch(() => {}));
}

// ---------------------------------------------------------------------------
// Nastavení: možnost odesílání vypnout
// ---------------------------------------------------------------------------

/** Malá rozbalovací lištička úplně dole v Nastavení: informace a možnost vypnutí. */
export function StatsSetting() {
  // místní stav – zaškrtnutí reaguje hned, bez čekání na databázi
  const [off, setOff] = useState<boolean | null>(null);
  useEffect(() => {
    getSetting<boolean>(OFF).then((v) => setOff(!!v));
  }, []);
  if (off == null) return null;
  return (
    <details className="mini-details">
      <summary>Anonymní statistika</summary>
      <p>
        Aby autor věděl, kolik lidí Úlník používá, aplikace občas pošle jen anonymní počet: „nainstalováno“ a „použito tento den/týden/měsíc“,
        typ telefonu, verzi a druh licence. Nikdy žádné jméno, data o včelstvech, polohu ani nic, podle čeho by šlo poznat vás nebo váš
        telefon.
      </p>
      <label className="check">
        <input
          type="checkbox"
          checked={!off}
          onChange={async (e) => {
            const on = e.target.checked;
            setOff(!on);
            await setSetting(OFF, !on);
            saved(on ? 'Statistika zapnuta' : 'Statistika vypnuta');
          }}
        />
        Odesílat anonymní statistiku
      </label>
    </details>
  );
}

// ---------------------------------------------------------------------------
// Pro autora: přehled (#/pripominky/statistiky)
// ---------------------------------------------------------------------------

interface Row {
  date: string;
  e: Ev;
  n: number;
  p?: Record<string, number>;
  s?: Record<string, number>;
  v?: Record<string, number>;
  l?: Record<string, number>;
}

const sum = (rows: Row[]) => rows.reduce((a, r) => a + r.n, 0);
function merge(rows: Row[], dim: 'p' | 's' | 'v' | 'l') {
  const out: Record<string, number> = {};
  for (const r of rows) for (const [k, v] of Object.entries(r[dim] ?? {})) out[k] = (out[k] ?? 0) + v;
  return Object.entries(out).sort((a, b) => b[1] - a[1]);
}
const LABEL: Record<string, string> = {
  ios: 'iPhone / iPad',
  android: 'Android',
  other: 'jiné (počítač)',
  plocha: 'z ikony na ploše',
  prohlizec: 'jen v prohlížeči',
  trial: 'zkušební sezóna',
  full: 'trvalá licence',
  annual: 'roční licence',
  expired: 'po vypršení',
};
const MONTHS = ['leden', 'únor', 'březen', 'duben', 'květen', 'červen', 'červenec', 'srpen', 'září', 'říjen', 'listopad', 'prosinec'];

function Split({ title, items }: { title: string; items: [string, number][] }) {
  if (!items.length) return null;
  return (
    <div className="stat-split">
      <h3>{title}</h3>
      <ul>
        {items.map(([k, v]) => (
          <li key={k}>
            <span>{LABEL[k] ?? k}</span>
            <strong>{v}</strong>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function StatsScreen() {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = async () => {
    setError(null);
    let key = '';
    try {
      key = localStorage.getItem('vcely-feedback-admin') ?? '';
    } catch {
      /* nic */
    }
    if (!key) return setError('Nejdřív otevřete Schránku připomínek a zadejte heslo.');
    const r = await fetch(STATS_URL, { headers: { Authorization: `Bearer ${key}` } }).catch(() => null);
    if (!r) return setError('Statistiku se nepodařilo načíst. Je telefon připojený?');
    if (r.status === 401) return setError('Heslo nesedí – zadejte ho znovu ve Schránce připomínek.');
    if (r.status === 404) return setError('Schránka v Cloudflare ještě nemá statistiku – vložte do workeru novou verzi kódu.');
    if (!r.ok) return setError(`Chyba ${r.status}.`);
    setRows(((await r.json()) as { rows: Row[] }).rows);
  };
  useEffect(() => {
    void load();
  }, []);

  const today = todayISO();
  const ym = today.slice(0, 7);
  const week = isoWeek(today);
  const of = (e: Ev, f: (r: Row) => boolean = () => true) => (rows ?? []).filter((r) => r.e === e && f(r));
  const installs = of('install');
  const monthRows = of('month', (r) => r.date.startsWith(ym));
  const months = [...new Set((rows ?? []).map((r) => r.date.slice(0, 7)))].sort().reverse().slice(0, 12);

  return (
    <Screen title="Statistika používání" crumbs={[['Schránka', 'pripominky/schranka']]} backTo="pripominky/schranka">
      <button className="btn" onClick={() => void load()}>
        <Icon name="refresh" size={20} /> Obnovit
      </button>
      {error && <Notice tone="warn">{error}</Notice>}
      {rows && rows.length === 0 && <Empty>Zatím žádná data – počítají se jen telefony s novou verzí aplikace.</Empty>}
      {rows && rows.length > 0 && (
        <>
          <div className="stat-tiles">
            <div className="stat-tile">
              <span>Telefonů celkem</span>
              <strong>{sum(installs)}</strong>
            </div>
            <div className="stat-tile">
              <span>Aktivní dnes</span>
              <strong>{sum(of('day', (r) => r.date === today))}</strong>
            </div>
            <div className="stat-tile">
              <span>Aktivní tento týden</span>
              <strong>{sum(of('week', (r) => isoWeek(r.date) === week))}</strong>
            </div>
            <div className="stat-tile">
              <span>Aktivní tento měsíc</span>
              <strong>{sum(monthRows)}</strong>
            </div>
          </div>
          <p className="small muted">
            „Telefonů celkem“ = kolik telefonů Úlník aspoň jednou otevřelo (i jen v prohlížeči). „Aktivní“ = telefon, na kterém byl Úlník v daném období aspoň jednou otevřený s internetem. Telefony, které používají aplikaci jen bez
            signálu, se započítají, až budou online.
          </p>

          <Section title="Po měsících">
            <div className="table-wrap">
              <table className="log">
                <thead>
                  <tr>
                    <th>Měsíc</th>
                    <th>Nové telefony</th>
                    <th>Aktivní</th>
                  </tr>
                </thead>
                <tbody>
                  {months.map((m) => (
                    <tr key={m}>
                      <td>
                        {MONTHS[Number(m.slice(5)) - 1]} {m.slice(0, 4)}
                      </td>
                      <td>{sum(of('install', (r) => r.date.startsWith(m)))}</td>
                      <td>{sum(of('month', (r) => r.date.startsWith(m)))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          <Section title={`Aktivní tento měsíc (${plural(sum(monthRows), 'telefon', 'telefony', 'telefonů')})`}>
            <Split title="Telefon" items={merge(monthRows, 'p')} />
            <Split title="Spouští" items={merge(monthRows, 's')} />
            <Split title="Licence" items={merge(monthRows, 'l')} />
            <Split title="Verze aplikace" items={merge(monthRows, 'v')} />
          </Section>

          <Section title="Všechny instalace">
            <Split title="Telefon" items={merge(installs, 'p')} />
            <Split title="Při instalaci spuštěno" items={merge(installs, 's')} />
          </Section>
        </>
      )}
    </Screen>
  );
}
